import React from 'react';
import PropTypes from 'prop-types';
import { LuBoxes, LuCalendarDays, LuCheck, LuCircleCheck, LuClipboardList, LuInfo, LuLayers, LuPencil, LuPlus, LuWallet, LuWarehouse, LuWrench } from 'react-icons/lu';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import { Field, SelectInput, TextInput } from '../../../components/form/FormControls';
import { HelpNote } from '../../ProfilePage/components/ProfileUi';
import StockScopeFields, { StockOwnershipField } from '../StockScopeFields';
import useStockText from '../stockText';

// Stok tipi listesinde gösterilmeyen eski adlar (kategori ile karışıyordu)
const HIDDEN_TYPES = new Set(['Sarf Malzemesi', 'Demirbaş']);
const CURRENCIES = ['TRY', 'USD', 'EUR'];

const Section = ({ icon: Icon, title, required, children }) => {
  const { label } = useStockText();
  return (
    <section className="tw-space-y-3">
      <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
        <span className="tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-lg tw-bg-slate-900/[0.05] tw-text-slate-500 dark:tw-bg-white/[0.07] dark:tw-text-slate-300">
          <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
        </span>
        {title}
        {required && <span className="tw-rounded-full tw-bg-rose-500/10 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-rose-600 dark:tw-text-rose-300">{label('form.required')}</span>}
      </h3>
      {children}
    </section>
  );
};

Section.propTypes = { icon: PropTypes.elementType.isRequired, title: PropTypes.string.isRequired, required: PropTypes.bool, children: PropTypes.node };

const CATEGORY_CARDS = [
  { value: 'sarf', icon: LuBoxes, active: 'tw-border-teal-500/60 tw-bg-teal-500/[0.07] dark:tw-border-teal-400/50', iconTone: 'tw-from-teal-400 tw-to-cyan-600' },
  { value: 'demirbas', icon: LuWrench, active: 'tw-border-violet-500/60 tw-bg-violet-500/[0.07] dark:tw-border-violet-400/50', iconTone: 'tw-from-violet-400 tw-to-indigo-600' },
];

/**
 * "Yeni Stok Ekle" and "Stok Düzenle" (same form): category, basic info with
 * customer/project, quantities and warehouse, pricing (profit rate is worked
 * out from the prices), dates and, for fixtures, ownership and depreciation.
 */
const StockFormModal = ({ mode, isOpen, stock, onChange, onScopeChange, onSave, onClose, stockTypes, customers, projects, projectsForCustomer, hasNoMatchingProject }) => {
  const { t, language, label } = useStockText();
  const isEdit = mode === 'edit';
  const field = (key) => (id) => ({ id, value: stock[key] || '', onChange: (e) => onChange(key, e.target.value) });
  const profit = stock['Kar Oranı(%)'];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={isEdit ? label('form.titleEdit') : label('form.titleAdd')}
      subtitle={isEdit ? (stock['Adı'] || label('form.subtitleEdit')) : label('form.subtitleAdd')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient={isEdit ? 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30' : 'tw-from-teal-400 tw-to-emerald-600 tw-shadow-teal-500/30'} size="xs">
          {isEdit ? <LuPencil aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>{label('common.cancel')}</Button>
          <Button variant="primary" onClick={onSave}>
            <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" strokeWidth={3} />
            {isEdit ? label('form.update') : label('form.add')}
          </Button>
        </>
      )}
    >
      <div className="tw-space-y-7">
        <Section icon={LuLayers} title={label('form.sections.category')} required>
          <div role="radiogroup" aria-label={label('form.sections.category')} className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
            {CATEGORY_CARDS.map((card) => {
              const selected = stock['Kategori'] === card.value;
              const Icon = card.icon;
              return (
                <button
                  key={card.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => onChange('Kategori', card.value)}
                  className={`tw-relative tw-flex tw-items-start tw-gap-3 tw-rounded-2xl tw-border tw-p-3.5 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${selected ? card.active : 'tw-border-slate-200 tw-bg-white/60 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.06]'}`}
                >
                  <span className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-gradient-to-br tw-text-white tw-shadow-md ${card.iconTone}`}>
                    <Icon aria-hidden="true" className="tw-h-5 tw-w-5" />
                  </span>
                  <span className="tw-min-w-0 tw-flex-1">
                    <span className="tw-block tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label(`categories.${card.value}`)}</span>
                    <span className="tw-mt-0.5 tw-block tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{label(`form.categoryDesc.${card.value}`)}</span>
                  </span>
                  {selected && <LuCircleCheck aria-hidden="true" className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-teal-600 dark:tw-text-teal-300" />}
                </button>
              );
            })}
          </div>
        </Section>

        <Section icon={LuInfo} title={label('form.sections.basic')}>
          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <Field label={label('form.name')} htmlFor="stockName" required>
              <TextInput {...field('Adı')('stockName')} placeholder={label('form.namePlaceholder')} />
            </Field>
            <Field label={label('columns.stockType')} htmlFor="stockType">
              <SelectInput
                id="stockType"
                value={stock['Stok Tipi'] || ''}
                onChange={(e) => onChange('Stok Tipi', e.target.value)}
                placeholder={label('form.typePlaceholder')}
                current={stock['Stok Tipi'] || ''}
                options={stockTypes.filter((type) => !HIDDEN_TYPES.has(type.name)).map((type) => ({ value: type.name, label: type.name }))}
              />
            </Field>
            {stockTypes.length === 0 && (
              <HelpNote tone="warning" className="sm:tw-col-span-2">{label('form.noTypes')}</HelpNote>
            )}
            <Field label={label('columns.status')} htmlFor="stockStatus">
              <SelectInput
                id="stockStatus"
                value={stock['Durum'] || ''}
                onChange={(e) => onChange('Durum', e.target.value)}
                placeholder={label('form.statusPlaceholder')}
                options={[{ value: 'aktif', label: label('status.aktif') }, { value: 'inaktif', label: label('status.inaktif') }]}
              />
            </Field>
            <Field label={label('form.info')} htmlFor="stockInfo">
              <TextInput {...field('Bilgi')('stockInfo')} placeholder={label('form.infoPlaceholder')} />
            </Field>
            {/* Stok bir müşteriye/projeye bağlanabilir. Raporlardaki "hangi müşteriye
                ne kadar gitti" hesabı yalnızca sarf malzemesini kapsar; demirbaşta
                müşteri yerine kendi firmamız da seçilebilir. */}
            <StockScopeFields
              stock={stock}
              onScopeChange={onScopeChange}
              customers={customers}
              projects={projects}
              projectsForCustomer={projectsForCustomer}
              hasNoMatchingProject={hasNoMatchingProject}
            />
          </div>
        </Section>

        <Section icon={LuWarehouse} title={label('form.sections.stock')}>
          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-3">
            <Field label={label('columns.quantity')} htmlFor="stockQuantity" required>
              <TextInput type="number" {...field('Miktar')('stockQuantity')} placeholder="0" className="tw-tabular-nums" />
            </Field>
            <Field label={label('form.remaining')} htmlFor="stockRemaining">
              <TextInput type="number" {...field('Kalan')('stockRemaining')} placeholder="0" className="tw-tabular-nums" />
            </Field>
            <Field label={label('columns.minQuantity')} htmlFor="stockMin" hint={label('form.minHint')}>
              <TextInput
                id="stockMin"
                type="number"
                min="0"
                placeholder="1"
                value={stock['Min Miktar'] ?? '1'}
                onChange={(e) => onChange('Min Miktar', e.target.value)}
                className="tw-tabular-nums"
              />
            </Field>
            <Field label={label('form.warehouse')} htmlFor="stockWarehouse" className="sm:tw-col-span-3">
              <TextInput {...field('Depo')('stockWarehouse')} placeholder={label('form.warehousePlaceholder')} />
            </Field>
          </div>
        </Section>

        <Section icon={LuWallet} title={label('form.sections.pricing')}>
          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <Field label={label('columns.purchasePrice')} htmlFor="stockPurchase">
              <TextInput type="number" {...field('Giriş Fiyatı')('stockPurchase')} placeholder="0.00" className="tw-tabular-nums" />
            </Field>
            <Field label={label('columns.salePrice')} htmlFor="stockSale">
              <TextInput type="number" {...field('Çıkış Fiyatı')('stockSale')} placeholder="0.00" className="tw-tabular-nums" />
            </Field>
            <Field label={label('columns.currency')} htmlFor="stockCurrency">
              <SelectInput
                id="stockCurrency"
                value={stock['Döviz Tipi'] || ''}
                onChange={(e) => onChange('Döviz Tipi', e.target.value)}
                placeholder={label('form.select')}
                options={CURRENCIES.map((code) => ({ value: code, label: `${code} - ${label(`form.currencies.${code}`)}` }))}
              />
            </Field>
            <Field label={label('columns.rate')} htmlFor="stockRate">
              <TextInput type="number" {...field('Döviz Kuru')('stockRate')} placeholder="1.00" className="tw-tabular-nums" />
            </Field>
            {profit && (
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50/80 tw-px-4 tw-py-3 sm:tw-col-span-2 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                <span className="tw-text-sm tw-font-medium tw-text-slate-600 dark:tw-text-slate-300">{label('form.profit')}</span>
                <span className={`tw-text-lg tw-font-bold tw-tabular-nums ${parseFloat(profit) >= 0 ? 'tw-text-emerald-600 dark:tw-text-emerald-300' : 'tw-text-rose-600 dark:tw-text-rose-300'}`}>%{profit}</span>
              </div>
            )}
          </div>
        </Section>

        <Section icon={LuCalendarDays} title={label('form.sections.dates')}>
          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <Field label={label('columns.startDate')} htmlFor="stockStart">
              <TextInput type="date" {...field('Kullanma Başlama Tarihi')('stockStart')} />
            </Field>
            <Field label={label('columns.expiryDate')} htmlFor="stockExpiry">
              <TextInput type="date" {...field('Son Kullanma Tarihi')('stockExpiry')} />
            </Field>
          </div>
        </Section>

        {/* Yalnızca demirbaş */}
        {stock['Kategori'] === 'demirbas' && (
          <Section icon={LuClipboardList} title={label('form.sections.fixture')}>
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <StockOwnershipField value={stock.ownershipType} onChange={(value) => onChange('ownershipType', value)} />
              <Field label={label('form.depreciation')} htmlFor="stockDepreciation" hint={label('form.depreciationHint')}>
                <TextInput type="number" {...field('Yıpranma Payı')('stockDepreciation')} placeholder={label('form.depreciationPlaceholder')} className="tw-tabular-nums" />
              </Field>
            </div>
          </Section>
        )}
      </div>
    </Modal>
  );
};

StockFormModal.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  isOpen: PropTypes.bool.isRequired,
  stock: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onScopeChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  stockTypes: PropTypes.array.isRequired,
  customers: PropTypes.array.isRequired,
  projects: PropTypes.array.isRequired,
  projectsForCustomer: PropTypes.func.isRequired,
  hasNoMatchingProject: PropTypes.func.isRequired,
};

export default StockFormModal;
